import { useEffect } from 'react'

import { useAppDispatch, useAppSelector } from '@/store'
import {
  fetchVersions,
  selectVersionsFor,
  selectVersionsListErrorFor,
  selectVersionsLoadedKey,
  selectVersionsLoading,
  selectVersionsNonce
} from '../slices/versionsSlice'
import type { VersionRow } from '../types'

/**
 * Load and return the versions of `projectId` — and ONLY that project's
 * (TASK-3D6DB8B6 fix cycle 1). Until its own read lands the list is empty,
 * never the previous project's rows, so a chip or a bulk-import target can
 * never carry another project's versionId.
 *
 * The load is dispatched on every project change, unconditionally:
 * `fetchVersions`'s keyed `shouldFetch` condition is what dedupes (a second
 * panel on the same page, StrictMode's double effect, a same-key read in
 * flight). The old `versions.length === 0` guard read a warm cache from
 * project A as "already loaded" for project B.
 *
 * Wake-up dependency, per the IntakesPanel pattern
 * (`px-fetchcache-inflight-veto-ignores-the-key`): `stuck` is true exactly
 * when this project's versions are neither loaded, in flight, nor recorded as
 * failed, so a different project's read settling wakes the effect. It never
 * depends on `loading` alone — a failure would then retry forever. The
 * versions nonce (a realtime `version.*` frame for this project) reloads too.
 */
export function useProjectVersions(orgId: string, projectId: string): readonly VersionRow[] {
  const dispatch = useAppDispatch()
  const versions = useAppSelector((s) => selectVersionsFor(s, projectId))
  const current = useAppSelector(selectVersionsLoadedKey) === projectId
  const loading = useAppSelector(selectVersionsLoading)
  const listError = useAppSelector((s) => selectVersionsListErrorFor(s, projectId))
  const nonce = useAppSelector(selectVersionsNonce)
  const stuck = !current && !loading && listError === null

  useEffect(() => {
    if (orgId === '' || projectId === '') return
    void dispatch(fetchVersions({ orgId, projectId }))
  }, [dispatch, orgId, projectId, stuck, nonce])

  return versions
}
